'use client';

import {
  Download,
  Database,
  Files,
  Eye,
  Handshake,
  CheckCircle,
  ArrowRight,
  Envelope,
  ShieldCheck,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { AnimatedSection } from '@/components/marketing/AnimatedSection';
import { IconCardGrid } from '@/components/marketing/IconCardGrid';
import { PageHero } from '@/components/marketing/PageHero';
import { getAppLink } from '@/lib/links';

const pledges = [
  {
    icon: Download,
    title: 'Self-serve export in settings',
    description:
      'Your tenant data is portable. From Settings → Account → Export my data you can download core CRM records as JSON today—no tickets, no waiting weeks.',
  },
  {
    icon: Eye,
    title: 'Forever-free read-only after cancel',
    description:
      'If you cancel, our pledge is free, read-only access to the history you built—so you can still review past work without an active paid subscription. Rollout of this mode continues alongside the full export package.',
  },
  {
    icon: Database,
    title: 'Core CRM tables available now',
    description:
      'Self-serve export today includes contacts, customers, leads, jobs, invoices, properties, estimates, proposals, and projects for your workspace. Notes, activities, and broader schema coverage remain on the pledge roadmap.',
  },
  {
    icon: Files,
    title: 'R2 documents & full package (roadmap)',
    description:
      'Documents stored in Cloudflare R2—photos, PDFs, signed proposals, and other attachments—are part of the full Data Freedom package we are building so you leave with both records and supporting files.',
  },
  {
    icon: Handshake,
    title: 'No hostage data',
    description:
      'We compete on product, not lock-in. You can leave with what is yours. We will not hold files or force a paid plan just to retrieve business records you already own.',
  },
  {
    icon: ShieldCheck,
    title: 'Your data, your control',
    description:
      'Paired with tenant isolation and security controls, the Data Freedom Pledge means you own the relationship with your customers—and the data that powers it—at every stage of the lifecycle.',
  },
];

const availableNow = [
  'Contacts, customers, and leads',
  'Jobs, invoices, and properties',
  'Estimates, proposals, and projects',
  'One-click JSON download from Settings → Account → Export my data',
];

const comingSoon = [
  'Notes, activities, and related child records',
  'Documents and media stored in R2 for your tenant',
  'Workspace configuration and broader schema coverage',
  'Verified forever-free read-only access after cancel',
];

const dataFreedomSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Data Freedom Pledge — Rooflyzer',
  description:
    'Rooflyzer Data Freedom Pledge: self-serve export of core CRM tables today, expanding to full tenant data and R2 documents, plus forever-free read-only access after cancel.',
  url: 'https://rooflyzer.pro/data-freedom',
};

const dataFreedomArticleSchema = {
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: 'Rooflyzer Data Freedom Pledge',
  about: ['data export', 'data portability', 'read-only access', 'tenant data', 'R2 documents'],
  description:
    'Rooflyzer pledges portable tenant data: core CRM export is available in app settings now; full database plus R2 document export and post-cancel read access remain the product commitment.',
  url: 'https://rooflyzer.pro/data-freedom',
  author: { '@type': 'Organization', name: 'Rooflyzer' },
  publisher: { '@type': 'Organization', name: 'Rooflyzer' },
};

export const DataFreedomPledgePage = () => {
  return (
    <>
      <SEOHead
        title="Data Freedom Pledge — Your Data, Always Yours"
        description="Export core CRM records from Settings today. Rooflyzer’s Data Freedom Pledge commits to portable tenant data, expanding full export and forever-free read access after cancel."
        canonicalUrl="/data-freedom"
        keywords="data export, data portability, roofing CRM export, cancel subscription data access, tenant data export"
        ogImage="/social-card.svg"
        schema={[dataFreedomSchema, dataFreedomArticleSchema]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Data Freedom Pledge', url: '/data-freedom' },
        ]}
      />

      <PageHero
        badgeIcon={Download}
        badgeLabel="Data Freedom Pledge"
        title={
          <>
            Your data is yours.{' '}
            <span className="text-gradient-gold">Portable now. Expanding to the full package.</span>
          </>
        }
        lead={
          <>
            We will never hold your business hostage. Export core CRM records from Settings today,
            and we continue building toward complete tenant export (including documents) and
            forever-free read-only access after cancel.
          </>
        }
      />

      <IconCardGrid items={pledges} />

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="rounded-3xl bg-white/[0.03] border border-white/[0.08] p-8 md:p-12 overflow-hidden">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider mb-6">
                <Database className="w-4 h-4 text-emerald-400" />
                <span>What you can export</span>
              </div>
              <h2 className="section-h2 font-bold text-white mb-4">
                Available now vs the full pledge
              </h2>
              <p className="body-lg text-white/60 max-w-3xl mb-8">
                Self-serve export in the app covers core CRM tables today. The broader Data Freedom
                package—more entities, R2 documents, and verified post-cancel read access—is the
                direction we are shipping toward, not a claim that every item is live yet.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
                <div>
                  <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-emerald-400 mb-3">
                    Available now
                  </h3>
                  <ul className="space-y-3">
                    {availableNow.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-white/70">
                        <CheckCircle
                          className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5"
                          weight="bold"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-white/50 mb-3">
                    Pledge direction (expanding)
                  </h3>
                  <ul className="space-y-3">
                    {comingSoon.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-white/60">
                        <CheckCircle
                          className="w-5 h-5 text-white/35 flex-shrink-0 mt-0.5"
                          weight="bold"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="rounded-3xl bg-white/[0.03] border border-white/[0.08] p-8 md:p-12 flex flex-col lg:flex-row items-center gap-10 overflow-hidden">
              <div className="flex-1 space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider">
                  <Envelope className="w-4 h-4 text-emerald-400" />
                  <span>How to export</span>
                </div>
                <h2 className="section-h2 font-bold text-white">
                  One click in the app—or contact us
                </h2>
                <p className="body-lg text-white/60">
                  Prefer self-serve? Open workspace settings and download core CRM records as JSON.
                  Need help or a broader handoff while the full package expands? Email us and we
                  will help.
                </p>
                <ol className="space-y-3 text-white/70 list-decimal list-inside">
                  <li>Sign in to your workspace.</li>
                  <li>Go to Settings → Account → Export my data (or request help from support).</li>
                  <li>
                    Download core records now; R2 documents and broader schema remain on the
                    roadmap.
                  </li>
                  <li>
                    Keep building with us—or reach out if you need export help while the full
                    package lands.
                  </li>
                </ol>
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <Button
                    size="lg"
                    className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                    asChild
                  >
                    <a href={getAppLink('/app/settings?tab=account&subTab=data-export')}>
                      Open export in app
                      <ArrowRight className="ml-2 size-5" />
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-lg border border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800/60 px-8"
                    asChild
                  >
                    <Link to="/contact">Request export help</Link>
                  </Button>
                </div>
              </div>
              <div className="lg:w-2/5">
                <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-8 space-y-4">
                  <h3 className="text-lg font-bold text-white">After you cancel</h3>
                  <p className="text-white/60 leading-relaxed">
                    Our pledge is that paid features stop without locking you out of history you
                    already built. Forever-free read-only access is part of that commitment and is
                    being hardened alongside the full export package.
                  </p>
                  <ul className="space-y-2 text-sm text-white/70">
                    {[
                      'Core export available from settings while active',
                      'No hostage data—portable records by design',
                      'Broader package + post-cancel read on the roadmap',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <CheckCircle
                          className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5"
                          weight="bold"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};
